import { IMAGES } from "@/data/brand";

// SEO-focused resource articles. Content is an array of blocks rendered by BlogPost.
export const POSTS = [
  {
    slug: "how-to-teach-stop-drop-and-roll",
    title: "How to Teach Kids Stop, Drop and Roll (Step-by-Step)",
    excerpt: "A simple, kid-friendly way to teach the life-saving Stop, Drop and Roll technique — plus how to make it stick.",
    date: "2026-01-12",
    author: "Bobby Martin",
    readTime: "4 min read",
    keywords: "stop drop and roll for kids, fire safety for kids, teach children fire safety",
    image: IMAGES.toddlerFire,
    content: [
      { type: "p", text: "Stop, Drop and Roll is one of the first fire-safety skills every child should learn. If clothing ever catches fire, these three simple moves can smother the flames and prevent serious burns. The good news: kids learn it fast when you make it fun." },
      { type: "h", text: "The three steps" },
      { type: "ol", items: [
        "STOP right where you are — running only feeds the flames with more air.",
        "DROP to the ground and cover your face with your hands to protect it.",
        "ROLL over and over until the fire is completely out.",
      ] },
      { type: "h", text: "Make it stick with practice" },
      { type: "p", text: "Turn it into a game. Practice on a soft rug, call out 'Fire!' and have your child perform all three steps. Repetition builds muscle memory, so a child reacts correctly under stress. Singing a short song while rolling helps younger kids remember the sequence." },
      { type: "h", text: "Common mistakes to avoid" },
      { type: "ul", items: [
        "Don't let kids run — it's the natural instinct but the most dangerous.",
        "Remind them to cover their face every time.",
        "Keep rolling until the flames are fully out, not just for a second.",
      ] },
      { type: "p", text: "Want a memorable way to reinforce the lesson? Our book and safety songs teach Stop, Drop and Roll alongside Bobby and Pluggy, so the habit sticks long after the lesson ends." },
    ],
  },
  {
    slug: "family-fire-safety-tips",
    title: "10 Fire Safety Tips Every Family Should Practice at Home",
    excerpt: "From smoke alarms to escape plans, these practical fire-safety tips help keep your whole family prepared.",
    date: "2026-01-20",
    author: "Bobby Martin",
    readTime: "5 min read",
    keywords: "home fire safety, fire safety tips for families, fire prevention for kids",
    image: IMAGES.heroScene,
    content: [
      { type: "p", text: "As a former firefighter, I've seen how a few simple habits prevent the majority of home fire tragedies. Prevention and preparation are everything. Here are ten fire-safety essentials every family should practice." },
      { type: "ol", items: [
        "Install smoke alarms on every level and test them monthly.",
        "Replace smoke-alarm batteries at least once a year.",
        "Create a home fire escape plan with two ways out of every room.",
        "Pick an outside meeting spot the whole family knows.",
        "Practice your escape plan twice a year — day and night.",
        "Teach kids to feel doors for heat before opening them.",
        "Show children how to stay low under smoke and get outside fast.",
        "Keep matches and lighters locked away from young children.",
        "Never leave cooking, candles, or space heaters unattended.",
        "Teach kids how and when to call 9-1-1.",
      ] },
      { type: "h", text: "Prevention is a family habit" },
      { type: "p", text: "Safety isn't a one-time conversation — it's a routine. Involve your kids in monthly alarm tests and escape drills so preparedness becomes second nature. When children feel like safety heroes, they take the lessons seriously." },
    ],
  },
  {
    slug: "seat-belt-safety-for-kids",
    title: "Seat Belt Safety for Kids: Building Lifelong Habits",
    excerpt: "Why every ride should start with a click — and how to make buckling up automatic for your children.",
    date: "2026-02-02",
    author: "Nekia Martin",
    readTime: "4 min read",
    keywords: "seat belt safety for kids, car safety for children, booster seat safety",
    image: IMAGES.seatbeltSafety,
    content: [
      { type: "p", text: "Seat belts are the single most effective way to protect children in a vehicle — but only when they're used correctly, every single time. Building the buckle-up habit early keeps kids safe for life." },
      { type: "h", text: "The rules that matter most" },
      { type: "ul", items: [
        "Buckle up before the car moves — no exceptions, even for short trips.",
        "Keep the lap belt low and snug across the hips, not the stomach.",
        "The shoulder belt should cross the chest, never under the arm or behind the back.",
        "Keep kids in a booster seat until the belt fits properly (usually around 4'9\").",
      ] },
      { type: "h", text: "Make it automatic" },
      { type: "p", text: "Consistency is key. When adults always buckle up first, kids follow. Praise your child for clicking in without a reminder, and make it part of the pre-drive routine so it becomes as automatic as closing the door." },
    ],
  },
  {
    slug: "crosswalk-safety-for-children",
    title: "Crosswalk Safety Rules Every Child Should Know",
    excerpt: "Teach your kids how to cross the street safely with these simple, memorable pedestrian-safety rules.",
    date: "2026-02-14",
    author: "Bobby Martin",
    readTime: "3 min read",
    keywords: "crosswalk safety for kids, pedestrian safety for children, street safety",
    image: IMAGES.crosswalkSafety,
    content: [
      { type: "p", text: "Walking is one of the first bits of independence kids earn — and knowing how to cross the street safely is a skill that protects them for life. Keep the rules simple and practice them together." },
      { type: "h", text: "The safe-crossing checklist" },
      { type: "ol", items: [
        "Always cross at a corner or a marked crosswalk.",
        "Stop at the curb — never step into the street without looking.",
        "Look LEFT, then RIGHT, then LEFT again before stepping out.",
        "Make eye contact with drivers so you know they see you.",
        "Walk, don't run, and keep looking as you cross.",
        "Put phones and headphones away while crossing.",
      ] },
      { type: "p", text: "Practice on real walks around your neighborhood. Narrate what you're doing — 'We stop, we look, we listen' — so your child internalizes the routine." },
    ],
  },
  {
    slug: "best-childrens-safety-books",
    title: "How to Choose the Best Children's Safety Books",
    excerpt: "What to look for in safety books that actually teach kids — and why story and character make lessons stick.",
    date: "2026-02-25",
    author: "Nekia Martin",
    readTime: "4 min read",
    keywords: "children's safety books, best safety books for kids, fire safety books for children",
    image: IMAGES.bookCover,
    content: [
      { type: "p", text: "Not all children's safety books are created equal. The best ones don't just list rules — they wrap real, actionable lessons inside a story kids want to read again and again." },
      { type: "h", text: "What to look for" },
      { type: "ul", items: [
        "Relatable characters kids connect with and want to imitate.",
        "One clear safety concept per book, taught step by step.",
        "Age-appropriate language and bright, engaging illustrations.",
        "Actionable takeaways parents can reinforce at home.",
        "A positive, empowering tone — safety as heroism, not fear.",
      ] },
      { type: "h", text: "Why story beats a lecture" },
      { type: "p", text: "Children remember what they feel. When a beloved character like Bobby or Pluggy models Stop, Drop and Roll, the lesson lands emotionally and sticks. That's the whole idea behind The Safety Quest series." },
    ],
  },
  {
    slug: "school-safety-programs-georgia",
    title: "Bringing Safety Programs to Schools & Daycares in Georgia",
    excerpt: "What a great safety assembly looks like — and how schools and daycares can bring one to their community.",
    date: "2026-03-08",
    author: "Bobby Martin",
    readTime: "5 min read",
    keywords: "school safety programs Georgia, daycare safety programs, fire safety assembly for schools",
    image: IMAGES.classroom,
    content: [
      { type: "p", text: "A great safety program does more than check a box — it gives children skills they carry for life, delivered in a way that's genuinely fun. Here's what sets an effective school or daycare safety program apart." },
      { type: "h", text: "What makes a program work" },
      { type: "ul", items: [
        "Age-appropriate content tailored to each grade level.",
        "Interactive, hands-on drills — not just a slideshow.",
        "Memorable characters and songs that reinforce the message.",
        "Clear takeaways for teachers and parents to continue at home.",
      ] },
      { type: "h", text: "Bringing a program to your community" },
      { type: "p", text: "The Safety Quest delivers localized, high-impact safety programs to schools and daycares across Georgia — complete with interactive lessons, books, and appearances from Bobby and Pluggy. If you'd like to bring one to your school, reach out through our contact page and we'll help you plan it." },
    ],
  },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
